import React, { useState, useMemo, } from "react";
import FocusTrap from "focus-trap-react";
import withForwardedRef from "./withForwardedRef";
import { ContextMenuOverlay, StyledOverlay } from "./withPortal.style";
import FloatingPortalContainer from "./FloatingPortalContainer";
import usePortal from "../hooks/usePortal";
import ConditionalWrapper from "../utils/ConditionalWrapper";
import useCursor from "../hooks/useCursor";
import useUniqueId from "../hooks/useUniqueId";
// makes alignment compatible to FloatingUI interface
const alignmentMap = {
    top: "top",
    left: "left",
    right: "right",
    bottom: "bottom",
    topLeft: "top-start",
    topRight: "top-end",
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    leftTop: "left-start",
    leftBottom: "left-end",
    rightTop: "right-start",
    rightBottom: "right-end",
};
// calculate the position value of the Tooltip, based on the placement of the portal
const tooltipPositionMap = {
    top: "bottom",
    left: "right",
    right: "left",
    bottom: "top",
    "top-start": "bottomLeft",
    "top-end": "bottomRight",
    "bottom-start": "topLeft",
    "bottom-end": "topRight",
    "left-start": "rightTop",
    "left-end": "rightBottom",
    "right-start": "leftTop",
    "right-end": "leftBottom",
};
export const PORTAL_DELAY_DURATION_MS = 300;
export const TRIGGER_CLICK = "click";
export const TRIGGER_RIGHT_CLICK = "right-click";
export const TRIGGER_HOVER = "hover";
const isClickTrigger = (trigger) => trigger === TRIGGER_CLICK || trigger === TRIGGER_RIGHT_CLICK;
const getDefaultOptions = () => ({
    alignment: "bottomLeft",
    offset: [0, 0],
    trigger: TRIGGER_HOVER,
    position: "absolute",
    flip: true,
    hide: true,
    sameWidth: false,
    disablePortal: false,
    container: null,
    shouldHideWhenTargetHidden: true,
    shouldCloseWhenTargetHidden: false,
    returnFocusOnDeactivate: true,
    useFocusTrap: true,
    allowTabNavigation: true,
    dialog: false,
});
export const withPortal = (PortalContent, options) => (Target) => function portal(props) {
    const { alignment, offset, position, flip, hide: hideOption, sameWidth: sameWidthOption, shouldHideWhenTargetHidden, shouldCloseWhenTargetHidden, trigger, container, disablePortal, id: defaultEventEmitId, dialog, returnFocusOnDeactivate, useFocusTrap, allowTabNavigation, } = Object.assign(Object.assign({}, getDefaultOptions()), options);
    const [isVisible, setIsVisible] = useState(false);
    const [targetRef, setTargetRef] = useState(null);
    const [portalRef, setPortalRef] = useState(null);
    const eventEmitId = useUniqueId("portal", defaultEventEmitId);
    const { update, placement, portalStyles, show, hide, getTargetProps, getPortalProps } = usePortal({
        onVisibleChange: (v) => {
            setIsVisible(v);
        },
        visible: isVisible,
        placement: alignmentMap[alignment],
        position,
        trigger,
        portalRef,
        targetRef,
        eventEmitId,
        portalOptions: {
            flip,
            minWidthTarget: false,
            hide: hideOption,
            sameWidthTarget: sameWidthOption,
            skidding: offset[0],
            distance: offset[1],
            shouldCloseWhenTargetHidden,
            shouldHideWhenTargetHidden,
            returnFocusOnHide: returnFocusOnDeactivate,
            trapFocus: useFocusTrap,
            allowTabNavigation,
            dialog,
        },
    });
    useCursor(targetRef);
    const utils = {
        tooltipPosition: tooltipPositionMap[placement],
        isVisible,
        show,
        hide,
        update,
    };
    const portalContent = (React.createElement(ConditionalWrapper, { condition: useFocusTrap && isClickTrigger(trigger), wrapper: (children) => (React.createElement(FocusTrap, { active: isVisible, focusTrapOptions: {
                escapeDeactivates: true,
                clickOutsideDeactivates: true,
                preventScroll: true,
                initialFocus: false,
                fallbackFocus: "#portal-content-wrapper",
                returnFocusOnDeactivate,
            } }, children)) }, overlayPortal(React.createElement(PortalContent, Object.assign({}, props, utils)), {
        setPortalRef,
        floatingStyles: portalStyles,
        props: getPortalProps(),
    })));
    // for right-click triggering, there is an extra overlay to disable the page scrolling
    const portalContentWithOverlay = (React.createElement(ConditionalWrapper, { condition: trigger === TRIGGER_RIGHT_CLICK, wrapper: (children) => React.createElement(ContextMenuOverlay, null, children) }, portalContent));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const TargetWithTriggerAndRef = useMemo(() => withForwardedRef(Target), [Target]);
    return (React.createElement(React.Fragment, null,
        React.createElement(TargetWithTriggerAndRef, Object.assign({ "data-portal-target": "true", "data-trigger-id": eventEmitId }, utils, { ref: setTargetRef }, getTargetProps(props))),
        isVisible ? (React.createElement(FloatingPortalContainer, { content: portalContentWithOverlay, container: container, disablePortal: disablePortal })) : undefined));
};
const overlayPortal = (portalContent, { setPortalRef, floatingStyles, props, }) => (React.createElement(StyledOverlay, Object.assign({ ref: setPortalRef }, props, { onClick: (e) => e.stopPropagation(), style: floatingStyles }),
    React.createElement("div", { id: "portal-content-wrapper" }, portalContent)));
//# sourceMappingURL=withPortal.js.map